const SELECT_DROPDOWN_SELECTOR = '.ant-select-dropdown:not(.ant-select-dropdown-hidden)';
const PICKER_DROPDOWN_SELECTOR = '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)';
const POPOVER_SELECTOR = '.ant-popover:not(.ant-popover-hidden)';
const INTERACTION_TRIGGER_SELECTOR = '.ant-select, .ant-picker, .ant-btn, button, [role="button"], .ant-dropdown-trigger';
const MANAGED_POPUP_CONTENT_SELECTOR = '.ant-popover, .ant-select-dropdown, .ant-picker-dropdown, .ant-dropdown';
const DROPDOWN_GAP = 6;
const MIN_VISIBLE_POPUP_HEIGHT = 120;

const getVisibleElements = (selector) => (
  Array.from(document.querySelectorAll(selector)).filter((element) => {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  })
);

const getVisibleTriggerElements = (selector) => (
  Array.from(document.querySelectorAll(selector)).filter((element) => {
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      rect.width > 0 &&
      rect.height > 0
    );
  })
);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const setStyleValue = (element, property, value) => {
  if (element.style[property] !== value) {
    element.style[property] = value;
  }
};

const positionPopup = (popup, trigger, options = {}) => {
  if (!popup || !trigger) return;

  const triggerRect = trigger.getBoundingClientRect();
  if (!triggerRect.width || !triggerRect.height) return;

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const popupRect = popup.getBoundingClientRect();
  const measuredWidth = popup.offsetWidth || popupRect.width || triggerRect.width;
  const measuredHeight = popup.offsetHeight || popupRect.height || 180;

  const width = Math.min(
    Math.max(measuredWidth, options.matchTriggerWidth ? triggerRect.width : 0),
    Math.max(triggerRect.width, viewportWidth - DROPDOWN_GAP * 2)
  );
  const preferredLeft = options.align === 'center'
    ? triggerRect.left + triggerRect.width / 2 - width / 2
    : triggerRect.left;
  const left = clamp(
    preferredLeft,
    DROPDOWN_GAP,
    Math.max(DROPDOWN_GAP, viewportWidth - width - DROPDOWN_GAP)
  );

  const spaceBelow = viewportHeight - triggerRect.bottom - DROPDOWN_GAP;
  const spaceAbove = triggerRect.top - DROPDOWN_GAP;
  const openAbove = spaceBelow < Math.min(measuredHeight, 180) && spaceAbove > spaceBelow;
  const availableHeight = Math.max(
    MIN_VISIBLE_POPUP_HEIGHT,
    Math.floor(openAbove ? spaceAbove : spaceBelow)
  );
  const height = Math.min(measuredHeight, availableHeight);
  const top = openAbove
    ? clamp(triggerRect.top - height - DROPDOWN_GAP, DROPDOWN_GAP, viewportHeight - DROPDOWN_GAP)
    : clamp(triggerRect.bottom + DROPDOWN_GAP, DROPDOWN_GAP, viewportHeight - height - DROPDOWN_GAP);

  popup.dataset.xinghuoPopupPositionFixed = 'true';
  setStyleValue(popup, 'position', 'fixed');
  setStyleValue(popup, 'inset', 'auto');
  setStyleValue(popup, 'left', `${Math.round(left)}px`);
  setStyleValue(popup, 'top', `${Math.round(top)}px`);
  setStyleValue(popup, 'right', 'auto');
  setStyleValue(popup, 'bottom', 'auto');
  setStyleValue(popup, 'minWidth', options.matchTriggerWidth ? `${Math.ceil(triggerRect.width)}px` : '');
  setStyleValue(popup, 'maxWidth', `calc(100vw - ${DROPDOWN_GAP * 2}px)`);
  setStyleValue(popup, 'maxHeight', `${availableHeight}px`);
  setStyleValue(popup, 'zIndex', '2200');
  setStyleValue(popup, 'transformOrigin', openAbove ? 'center bottom' : 'center top');
};

const getSelectTrigger = (popup, index, triggers) => {
  if (!triggers.length) return null;
  if (triggers.length === 1) return triggers[0];
  return triggers[index] || triggers[triggers.length - 1];
};

const getPickerTrigger = () => {
  const activePicker = document.activeElement?.closest?.('.ant-picker');
  if (activePicker) return activePicker;

  const focusedPickers = getVisibleTriggerElements('.ant-picker-focused');
  if (focusedPickers.length) return focusedPickers[focusedPickers.length - 1];

  const allPickers = getVisibleTriggerElements('.ant-picker');
  return allPickers[allPickers.length - 1] || null;
};

export const setupAntdPopupPositionFix = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__xinghuoAntdPopupPositionFix) return;

  let frameId = 0;
  let burstCount = 0;
  let lastInteractionElement = null;

  const isInsideManagedPopup = (element) => Boolean(
    element?.closest?.(MANAGED_POPUP_CONTENT_SELECTOR)
  );

  const getTriggerFromElement = (element) => {
    const trigger = element?.closest?.(INTERACTION_TRIGGER_SELECTOR);
    if (!trigger) return null;

    const rect = trigger.getBoundingClientRect();
    const style = window.getComputedStyle(trigger);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      rect.width <= 0 ||
      rect.height <= 0
    ) {
      return null;
    }

    return trigger;
  };

  const getPopoverTrigger = () => {
    if (!isInsideManagedPopup(document.activeElement)) {
      const activeTrigger = getTriggerFromElement(document.activeElement);
      if (activeTrigger) return activeTrigger;
    }

    return getTriggerFromElement(lastInteractionElement);
  };

  const reposition = () => {
    frameId = 0;

    const selectDropdowns = getVisibleElements(SELECT_DROPDOWN_SELECTOR);
    if (selectDropdowns.length) {
      const openSelects = getVisibleTriggerElements('.ant-select-open');
      selectDropdowns.forEach((popup, index) => {
        positionPopup(popup, getSelectTrigger(popup, index, openSelects), {
          matchTriggerWidth: true,
        });
      });
    }

    const pickerDropdowns = getVisibleElements(PICKER_DROPDOWN_SELECTOR);
    if (pickerDropdowns.length) {
      const pickerTrigger = getPickerTrigger();
      pickerDropdowns.forEach((popup) => positionPopup(popup, pickerTrigger));
    }

    const popovers = getVisibleElements(POPOVER_SELECTOR);
    if (popovers.length) {
      const popoverTrigger = getPopoverTrigger();
      popovers.forEach((popup) => positionPopup(popup, popoverTrigger, {
        align: 'center',
      }));
    }
  };

  const runBurst = () => {
    reposition();
    burstCount -= 1;
    if (burstCount > 0) {
      frameId = window.requestAnimationFrame(runBurst);
    }
  };

  const schedule = () => {
    if (frameId) return;
    frameId = window.requestAnimationFrame(reposition);
  };

  const scheduleBurst = () => {
    burstCount = Math.max(burstCount, 10);
    if (!frameId) {
      frameId = window.requestAnimationFrame(runBurst);
    }
  };

  const handleInteraction = (event) => {
    if (isInsideManagedPopup(event.target)) {
      scheduleBurst();
      return;
    }

    const trigger = getTriggerFromElement(event.target);
    if (trigger) {
      lastInteractionElement = trigger;
    }
    scheduleBurst();
  };

  const handleKeyDown = () => {
    if (!isInsideManagedPopup(document.activeElement)) {
      const trigger = getTriggerFromElement(document.activeElement);
      if (trigger) {
        lastInteractionElement = trigger;
      }
    }
    scheduleBurst();
  };

  const observer = new MutationObserver(scheduleBurst);
  observer.observe(document.body, {
    subtree: true,
    childList: true,
  });

  document.addEventListener('pointerdown', handleInteraction, true);
  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('focusin', handleInteraction, true);
  window.addEventListener('resize', schedule, true);
  document.addEventListener('scroll', schedule, true);

  window.__xinghuoAntdPopupPositionFix = {
    cleanup: () => {
      observer.disconnect();
      document.removeEventListener('pointerdown', handleInteraction, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleInteraction, true);
      window.removeEventListener('resize', schedule, true);
      document.removeEventListener('scroll', schedule, true);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      delete window.__xinghuoAntdPopupPositionFix;
    },
  };
};
